import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { Alert, RadioGroup } from '@ntconsult/aluma-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopiarEntregaParaOficial } from '../hooks';
import { CartaoPasso } from './CartaoPasso';
import { EstadoCidadeOficial } from './EnderecoOficialSection';
import { instantaneoEntrega, linhasEndereco, linhasExterior } from './formulario.utils';
import { OpcaoCartao } from './OpcaoCartao';
import { type CtxPasso, TITULO_PASSO } from './passo.types';

/** Pergunta 6 do roteiro: a entrega informada vira o endereço do cadastro? (R8) */
export function PassoMesmo({ ctx }: { ctx: CtxPasso }) {
  const { t } = useTranslation(['formulario', 'erros']);
  const { form, escolher, avancar, paises, estados, cidades } = ctx;
  const { getValues, setValue, watch } = form;
  const mesmo = watch('mesmo');
  const exterior = watch('origem') === 'exterior';
  const { copiar, cidadeNaoCasou, pronto } = useCopiarEntregaParaOficial({
    getValues,
    setValue,
    paises,
    estados,
  });
  const [copiando, setCopiando] = useState(false);
  // Axiis fora: sem a lista não há como copiar; só o "Não" (R8).
  const listaFora = paises.isError || (!exterior && estados.isError);
  const v = getValues();
  // Cidade (ou estado) sem correspondência: também ao voltar a este passo depois de copiar.
  const mostraEstadoCidade =
    mesmo === 'sim' &&
    !exterior &&
    !copiando &&
    ctx.copia.current !== null &&
    (cidadeNaoCasou || !watch('oficial.cidadeId'));
  const linhas = exterior
    ? linhasExterior(v.exterior)
    : linhasEndereco({ ...v.entrega, endereco: v.entrega.logradouro });

  async function sim() {
    escolher('mesmo', 'sim');
    setValue('oficialMarcado', true);
    // Já copiada e a entrega não mudou: só segue.
    if (ctx.copia.current === instantaneoEntrega(getValues())) return void ctx.continuar();
    setCopiando(true);
    const casou = await copiar();
    setCopiando(false);
    ctx.copia.current = instantaneoEntrega(getValues());
    // Cidade (ou estado) sem correspondência no Axiis: Estado e Cidade abrem no cartão, com Continuar.
    if (casou) avancar();
  }

  const escolha = (x: string) => {
    if (x === 'sim') return void sim();
    // "Não" desliga o oficial e o devolve à fotografia (R5, pela regra do caminho).
    escolher('mesmo', 'nao');
    avancar();
  };

  return (
    <CartaoPasso
      titulo={t('formulario:mesmo.pergunta')}
      onVoltar={ctx.onVoltar}
      onContinuar={
        mostraEstadoCidade
          ? ctx.continuar
          : ctx.respondido && !(mesmo === 'sim' && listaFora)
            ? () => escolha(mesmo)
            : undefined
      }
    >
      <div className="rounded-md border border-border p-4 text-foreground">
        {linhas.map((l) => (
          <p key={l}>{l}</p>
        ))}
      </div>
      {listaFora && (
        <Alert
          variant="danger"
          title={t(`erros:${mensagemDeErro(paises.error ?? estados.error)}`)}
        />
      )}
      <RadioGroup
        name="mesmo"
        aria-labelledby={TITULO_PASSO}
        className="grid gap-3 sm:grid-cols-2"
        value={mesmo}
        onValueChange={(x) => escolher('mesmo', x as 'sim' | 'nao')}
      >
        {!listaFora && (
          <OpcaoCartao
            value="sim"
            label={t('formulario:mesmo.sim')}
            onEscolher={escolha}
            indisponivel={!pronto || copiando}
          />
        )}
        <OpcaoCartao value="nao" label={t('formulario:mesmo.nao')} onEscolher={escolha} />
      </RadioGroup>
      {mostraEstadoCidade && (
        <div className="flex flex-col gap-4">
          <EstadoCidadeOficial
            setValue={setValue}
            errors={form.formState.errors}
            estadoId={watch('oficial.estadoId')}
            cidadeId={watch('oficial.cidadeId')}
            rotulos={ctx.rotulosCidade}
            estados={estados}
            cidades={cidades}
            cidadeNaoCasou
          />
        </div>
      )}
    </CartaoPasso>
  );
}
